import { FC, useState } from 'react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { createUseStyles } from 'react-jss';
import { Logger } from '../../support/Logger';
import { IUnreadableClient, UnreadableReason } from '@innerhive-internal/innerhive-common';
import { BRAND, CHART_INK } from './agencyChartTheme';

const useStyles = createUseStyles({
    panel: {
        padding: '11px 14px',
        borderRadius: 10,
        border: `1px solid ${BRAND.tealEdge}`,
        background: BRAND.tealWash,
        fontSize: 13,
        lineHeight: 1.5,
        color: CHART_INK.strongLabel
    },

    lead: {
        fontSize: 12.5,
        color: CHART_INK.label
    },

    rows: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 6,
        marginTop: 10
    },

    row: {
        display: 'flex',
        gap: 10,
        alignItems: 'baseline',
        flexWrap: 'wrap' as const
    },

    name: {
        fontWeight: 700
    },

    why: {
        fontSize: 12.5,
        color: CHART_INK.label
    },

    retry: {
        padding: '2px 10px',
        border: `1px solid ${BRAND.tealEdge}`,
        borderRadius: 6,
        background: 'none',
        font: 'inherit',
        fontSize: 12.5,
        color: BRAND.deepTeal,
        cursor: 'pointer',

        '&:disabled': {
            opacity: 0.6,
            cursor: 'default'
        },

        '&:hover:enabled': {
            background: 'rgba(0, 0, 0, 0.04)'
        }
    }
});

/**
 * What each reason means for the reader, and what resolves it.
 *
 * Written out per reason rather than assembled from parts because they are not variations on one
 * sentence: one is a thing to try, and the other two are different things to ask somebody else
 * for. Telling a reader a client is not shared when they, or the organization, hold a view-only
 * share sends them to ask for something they already have, so the two permission gaps are worded apart.
 * The not-shared wording follows the Clients tab, which reads the same signal and already says
 * "not shared with your organization ... Request access from the client".
 */
const WHY: { [key in UnreadableReason]: { reason: string; action?: string } } = {
    'not-shared': {
        reason: 'Not shared with your organization, so there is nothing here to read.',
        action: 'Request access from the client.'
    },
    'view-only-access': {
        reason: 'Shared with you or your organization as a viewer, so what is recorded on the profile is not included.',
        action: 'Ask the client for full access.'
    },
    'request-failed': {
        reason: 'Could not be read just now.'
    }
};

/**
 * The clients left out of every figure on the tab, named, with why and what to do about it.
 *
 * Named and reasoned rather than counted, because the reasons want opposite responses and a number
 * cannot tell them apart: retrying a permission gap achieves nothing, and waiting out a failed read
 * loses a client from the caseload in silence.
 *
 * Names are PHI, so each one carries mp-mask to keep it out of session replay. The reason and the
 * controls are not, and are deliberately left visible: a replay of somebody struggling with this
 * panel is only useful if it shows what the panel told them.
 */
/** One named client, why they are missing, and what resolves it. */
const UnreadableClientRow: FC<{ client: IUnreadableClient }> = ({ client }) => {
    const classes = useStyles();
    const { reason, action } = WHY[client.reason];

    return (
        <div className={classes.row} data-id="unreadable-client" data-reason={client.reason}>
            <span className={`${classes.name} mp-mask`} data-id="unreadable-client-name">{client.clientName}</span>
            <span className={classes.why} data-id="unreadable-client-reason">{reason}</span>
            {action && <span className={classes.why} data-id="unreadable-client-action">{action}</span>}
        </div>
    );
};

/**
 * The button a failed read offers, and what it says once it has been pressed.
 *
 * Owns its own attempt state so the panel above it stays about the clients. Whether the attempt
 * worked is read off the rows the parent re-renders with, not off the promise: a reread resolves
 * normally with the clients that failed inside it listed again, so a reader pressing the button
 * would otherwise watch the same rows reappear with nothing telling them it had happened.
 */
const RetryFailedReads: FC<{ onRetry: () => Promise<void> }> = ({ onRetry }) => {
    const classes = useStyles();
    const logger = useInstance(Logger);
    const [retrying, setRetrying] = useState(false);
    const [hasTriedAgain, setHasTriedAgain] = useState(false);

    const retry = async () => {
        setRetrying(true);
        setHasTriedAgain(false);
        try {
            await onRetry();
        } catch (err: any) {
            // The reread names the clients it could not read itself, so reaching here means it threw
            // for some other reason.
            logger.error(err, { context: 'UnreadableClients retry failed' });
        } finally {
            setRetrying(false);
            setHasTriedAgain(true);
        }
    };

    return (
        <div className={classes.rows}>
            <button
                type="button"
                className={classes.retry}
                data-id="unreadable-clients-retry"
                disabled={retrying}
                onClick={retry}
            >
                {retrying ? 'Trying again...' : 'Try again'}
            </button>
            {hasTriedAgain && (
                <span className={classes.why} data-id="unreadable-clients-retry-failed">
                    Still could not read them. Try again in a moment.
                </span>
            )}
        </div>
    );
};

export const UnreadableClients: FC<{
    clients: IUnreadableClient[];
    onRetry: () => Promise<void>;
}> = ({ clients, onRetry }) => {
    const classes = useStyles();

    if (clients.length === 0) {
        return null;
    }

    const hasFailedRead = clients.some(client => client.reason === 'request-failed');

    return (
        <div className={classes.panel} data-id="insights-unreadable-clients">
            <div className={classes.lead}>
                {clients.length === 1
                    ? 'One client is left out of every figure on this tab.'
                    : `${clients.length} clients are left out of every figure on this tab.`}
            </div>

            <div className={classes.rows}>
                {clients.map(client => (
                    <UnreadableClientRow key={client.careMapId} client={client} />
                ))}
            </div>

            {hasFailedRead && <RetryFailedReads onRetry={onRetry} />}
        </div>
    );
};
